import { useApolloClient } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkOff from "@mui/icons-material/LinkOff";
import Refresh from "@mui/icons-material/Refresh";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SuggestedField } from "components/ArtificialIntelligence/SuggestedField";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { ConfirmDialog } from "components/ConfirmDialog";
import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useState } from "react";
import { PROCESSED_MANIFESTS_QUERY } from "routes/Customer/Waste/Manifests/api";

import type { useManifestReviewSection } from "../hooks/useManifestReviewSection";

type ManifestDetailSectionProps = {
  manifest: ReturnType<typeof useManifestReviewSection>;
  documentId?: string;
  tenantId: string;
};

export function ManifestDetailSection({
  manifest,
  documentId,
  tenantId,
}: ManifestDetailSectionProps) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const { createDialog } = useMinimizedDialogs();
  const [showReExtractConfirm, setShowReExtractConfirm] = useState(false);
  const [showDetachConfirm, setShowDetachConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (manifest.manifestDataLoaded) {
    return (
      <Box sx={{ p: 1 }}>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Alert severity="info" variant="outlined">
            {manifest.manifestNeedsReview
              ? "We extracted data from this manifest document. Please review the AI-extracted values (marked with \u2728) and confirm."
              : "This is the raw data extracted from the manifest document. Edits here will update the waste manifest record and trigger reprocessing."}
          </Alert>

          {documentId && (
            <>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Re-run AI extraction on the source document, replacing all current manifest data">
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={() => setShowReExtractConfirm(true)}
                    variant="outlined"
                  >
                    Re-extract
                  </Button>
                </Tooltip>
                <Tooltip title="Remove the extracted manifest data but keep the source document">
                  <Button
                    size="small"
                    startIcon={<LinkOff />}
                    onClick={() => setShowDetachConfirm(true)}
                    variant="outlined"
                    color="warning"
                  >
                    Detach Data
                  </Button>
                </Tooltip>
                <Tooltip title="Permanently delete the source document and all associated manifest data">
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outlined"
                    color="error"
                  >
                    Delete Document
                  </Button>
                </Tooltip>
              </Stack>

              <ConfirmDialog
                open={showReExtractConfirm}
                onClose={() => setShowReExtractConfirm(false)}
                onConfirm={async () => {
                  try {
                    const jobs = await manifest.reExtractDocument(documentId, tenantId);
                    const jobId = jobs?.[0]?.job.id;
                    if (jobId) {
                      const dialogId = `manifest-reextract-${jobId}`;
                      createDialog({
                        id: dialogId,
                        dialogType: DialogType.DocumentAIAnalyzing,
                        props: {
                          open: true,
                          jobId,
                          preferOriginalTitle: true,
                          dialogId,
                          onComplete: () => {
                            client.refetchQueries({
                              include: [PROCESSED_MANIFESTS_QUERY],
                            });
                          },
                        },
                      });
                      // Close the review dialog — the analyzing dialog takes over
                      manifest.onClose();
                    }
                  } catch (err) {
                    alerts.error(
                      "Failed to queue re-extraction.",
                      err instanceof Error ? err : undefined
                    );
                    setShowReExtractConfirm(false);
                  }
                }}
                header="Re-extract manifest document?"
                info={
                  <>
                    This will re-extract data from the source document and replace all existing
                    manifest data. Any changes made since the last extraction will be overwritten.
                    {(manifest.manifestWatch("manifestTrackingNumber") ||
                      manifest.manifestWatch("generatorId")) && (
                      <>
                        <br />
                        <br />
                        The following values will be preserved during re-extraction:
                        {manifest.manifestWatch("manifestTrackingNumber") && (
                          <>
                            <br />
                            <strong>MTN:</strong> {manifest.manifestWatch("manifestTrackingNumber")}
                          </>
                        )}
                        {manifest.manifestWatch("generatorId") && (
                          <>
                            <br />
                            <strong>Generator ID:</strong> {manifest.manifestWatch("generatorId")}
                          </>
                        )}
                      </>
                    )}
                  </>
                }
                confirmText="Re-extract"
              />

              <ConfirmDialog
                open={showDetachConfirm}
                onClose={() => setShowDetachConfirm(false)}
                onConfirm={async () => {
                  try {
                    await manifest.handleDetached();
                    alerts.success("Manifest data detached. The source document has been kept.");
                  } catch (err) {
                    alerts.error(
                      "Failed to detach manifest data.",
                      err instanceof Error ? err : undefined
                    );
                    setShowDetachConfirm(false);
                  }
                }}
                header="Detach manifest data?"
                info="This will delete the extracted manifest data (waste manifest, waste lines, and transporters) but keep the source document. The document can be re-extracted later."
                confirmText="Detach"
                loading={manifest.deletingManifest}
              />

              <ConfirmDialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={async () => {
                  try {
                    await manifest.handleDeleteDocument();
                  } catch (err) {
                    alerts.error(
                      "Failed to delete document.",
                      err instanceof Error ? err : undefined
                    );
                    setShowDeleteConfirm(false);
                  }
                }}
                header="Delete document?"
                info="This will permanently delete the source document and all associated manifest data (waste manifest, waste lines, and transporters). This cannot be undone."
                confirmText="Delete"
                loading={manifest.deletingDocument || manifest.deletingManifest}
              />
            </>
          )}

          {/* Manifest-level fields */}
          <Typography variant="subtitle1" fontWeight="bold">
            Hazardous Waste Manifest
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="manifestTrackingNumber"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Tracking Number"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="shippedDate"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Shipped Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <SuggestedTextField
              name="receivedDate"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Received Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          {manifest.trackingNumberChanged && (
            <Alert severity="warning" variant="outlined">
              Changing the tracking number will re-associate this manifest data with a different
              processed manifest record. A re-extraction of the source document will create a new
              record under the original tracking number.
            </Alert>
          )}

          {/* Generator */}
          <Typography variant="subtitle1" fontWeight="bold">
            Generator
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorId"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="EPA ID"
              size="small"
              sx={{ maxWidth: 200 }}
            />
            <SuggestedTextField
              name="generatorName"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailStreet1"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Mailing Street"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorMailStreet2"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Mailing Street 2"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorMailCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorMailState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="State"
              size="small"
              sx={{ minWidth: 80, flex: "0 1 auto" }}
            />
            <SuggestedTextField
              name="generatorMailZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ minWidth: 100, flex: "0 1 auto" }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorLocationStreet1"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Site Street"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorLocationStreet2"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Site Street 2"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorLocationCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorLocationState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="State"
              size="small"
              sx={{ minWidth: 80, flex: "0 1 auto" }}
            />
            <SuggestedTextField
              name="generatorLocationZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ minWidth: 100, flex: "0 1 auto" }}
            />
          </Stack>

          {/* Destination Facility */}
          <Typography variant="subtitle1" fontWeight="bold">
            Destination Facility (TSDF)
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacilityId"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="EPA ID"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacilityName"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacLocationStreet1"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Street"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacLocationCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacLocationState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="State"
              size="small"
              sx={{ minWidth: 80, flex: "0 1 auto" }}
            />
            <SuggestedTextField
              name="desFacLocationZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ minWidth: 100, flex: "0 1 auto" }}
            />
          </Stack>

          {/* Waste Lines */}
          <Typography variant="subtitle1" fontWeight="bold">
            Line Items ({manifest.wasteLineFieldArray.fields.length})
          </Typography>
          {manifest.wasteLineFieldArray.fields.map((field, index) => {
            const lineValues = manifest.manifestWatch(`wasteLines.${index}`);
            const isHazardous = lineValues.dotHazardous;
            const description = isHazardous
              ? lineValues.dotPrintedInformation
              : lineValues.nonHazWasteDescription;
            return (
              <Accordion key={field.id} defaultExpanded={index === 0}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
                    <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
                      Line {field.wasteLineNumber}
                    </Typography>
                    {isHazardous && (
                      <WarningAmber
                        color="warning"
                        sx={{ fontSize: 18, flexShrink: 0 }}
                        titleAccess="Hazardous"
                      />
                    )}
                    {description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        {" \u2014 "}
                        {description}
                      </Typography>
                    )}
                    {!description && <Box flex={1} />}
                    <IconButton
                      component="div"
                      role="button"
                      tabIndex={0}
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        manifest.onDeleteWasteLine(field.wasteLineNumber);
                        manifest.wasteLineFieldArray.remove(index);
                      }}
                      title="Remove waste line"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <SuggestedField
                        name={`wasteLines.${index}.dotHazardous`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                      >
                        {({ field, isSuggested, sparkleIcon }) => (
                          <Stack direction="row" alignItems="center">
                            <FormControlLabel
                              control={
                                <Checkbox checked={!!field.value} onChange={field.onChange} />
                              }
                              label="DOT Hazardous"
                            />
                            {isSuggested && sparkleIcon}
                          </Stack>
                        )}
                      </SuggestedField>
                      <SuggestedTextField
                        name={`wasteLines.${index}.dotIdNumberDescription`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="DOT ID/Description"
                        size="small"
                        fullWidth
                      />
                    </Stack>
                    <SuggestedTextField
                      name={`wasteLines.${index}.dotPrintedInformation`}
                      control={manifest.manifestControl}
                      needsReview={manifest.manifestNeedsReview}
                      label="DOT Printed Information"
                      size="small"
                      fullWidth
                    />
                    <SuggestedTextField
                      name={`wasteLines.${index}.nonHazWasteDescription`}
                      control={manifest.manifestControl}
                      needsReview={manifest.manifestNeedsReview}
                      label="Non-Haz Waste Description"
                      size="small"
                      fullWidth
                    />
                    <Stack direction="row" spacing={2}>
                      <SuggestedField
                        name={`wasteLines.${index}.containerNumber`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                      >
                        {({ field, isSuggested, sparkleAdornment }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === "" ? null : safeParseInt(e.target.value)
                              )
                            }
                            label="Container #"
                            type="number"
                            size="small"
                            sx={{ maxWidth: 120 }}
                            slotProps={{
                              input: {
                                startAdornment: isSuggested ? sparkleAdornment : undefined,
                              },
                            }}
                          />
                        )}
                      </SuggestedField>
                      <SuggestedTextField
                        name={`wasteLines.${index}.containerTypeCode`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="Container Type"
                        size="small"
                        sx={{ maxWidth: 150 }}
                      />
                      <SuggestedField
                        name={`wasteLines.${index}.quantity`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                      >
                        {({ field, isSuggested, sparkleAdornment }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === "" ? null : safeParseFloat(e.target.value)
                              )
                            }
                            label="Quantity"
                            type="number"
                            size="small"
                            sx={{ maxWidth: 150 }}
                            slotProps={{
                              input: {
                                startAdornment: isSuggested ? sparkleAdornment : undefined,
                              },
                            }}
                          />
                        )}
                      </SuggestedField>
                      <SuggestedTextField
                        name={`wasteLines.${index}.qtyUnitOfMeasureCode`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="UOM Code"
                        size="small"
                        sx={{ maxWidth: 120 }}
                      />
                      <SuggestedTextField
                        name={`wasteLines.${index}.managementMethodCode`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="Mgmt Method"
                        size="small"
                        sx={{ maxWidth: 150 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <SuggestedTextField
                        name={`wasteLines.${index}.federalWasteCodes`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="Federal Waste Codes"
                        size="small"
                        fullWidth
                        helperText="Comma-separated"
                      />
                      <SuggestedTextField
                        name={`wasteLines.${index}.stateWasteCodes`}
                        control={manifest.manifestControl}
                        needsReview={manifest.manifestNeedsReview}
                        label="State Waste Codes"
                        size="small"
                        fullWidth
                        helperText="Comma-separated"
                      />
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const nextLineNumber = String(
                Math.max(
                  0,
                  ...manifest.wasteLineFieldArray.fields.map(
                    (f) => safeParseInt(f.wasteLineNumber) ?? 0
                  )
                ) + 1
              );
              manifest.wasteLineFieldArray.append({
                wasteLineNumber: nextLineNumber,
                dotHazardous: false,
                dotIdNumberDescription: "",
                dotPrintedInformation: "",
                nonHazWasteDescription: "",
                containerNumber: null,
                containerTypeCode: "",
                quantity: null,
                qtyUnitOfMeasureCode: "",
                managementMethodCode: "",
                federalWasteCodes: "",
                stateWasteCodes: "",
              });
            }}
          >
            Add Waste Line
          </Button>

          {/* Transporters */}
          <Typography variant="subtitle1" fontWeight="bold">
            Transporters ({manifest.transporterFieldArray.fields.length})
          </Typography>
          {manifest.transporterFieldArray.fields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{field.transporterLineNumber}.</Typography>
              <SuggestedTextField
                name={`transporters.${index}.transporterId`}
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="EPA ID"
                size="small"
                sx={{ minWidth: 150 }}
              />
              <SuggestedTextField
                name={`transporters.${index}.transporterName`}
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="Name"
                size="small"
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => {
                  manifest.onDeleteTransporter(field.transporterLineNumber);
                  manifest.transporterFieldArray.remove(index);
                }}
                title="Remove transporter"
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const nextLineNumber = String(
                Math.max(
                  0,
                  ...manifest.transporterFieldArray.fields.map(
                    (f) => safeParseInt(f.transporterLineNumber) ?? 0
                  )
                ) + 1
              );
              manifest.transporterFieldArray.append({
                transporterLineNumber: nextLineNumber,
                transporterId: "",
                transporterName: "",
              });
            }}
          >
            Add Transporter
          </Button>
        </Stack>
      </Box>
    );
  }

  if (manifest.manifestLoading) {
    return <Skeleton variant="rectangular" height={200} sx={{ my: 2, gridColumn: "1 / -1" }} />;
  }

  return (
    <Alert severity="info" variant="outlined" sx={{ my: 2, gridColumn: "1 / -1" }}>
      This document has been classified as a waste manifest. Manifest data will be extracted after
      you confirm, and you can review it from the Waste Manifests page.
    </Alert>
  );
}
